import { useEffect, useState, useCallback } from "react";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { FloppyDisk, CalendarBlank, ArrowClockwise, CheckCircle } from "@phosphor-icons/react";
import { SourceTag } from "./PerfUI";
import { prettyFormula } from "./PerfCatalogue";

const UNIT_LABEL = { number: "nombre", currency: "€", percent: "%", duration: "durée" };
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function PerfSaisie({ services, metrics, collaborators, onSaved }) {
  const [date, setDate] = useState(todayISO());
  const [mode, setMode] = useState("service");
  const [values, setValues] = useState({});
  const [initial, setInitial] = useState({});
  const [meta, setMeta] = useState({ saved: false, saved_at: null });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/performance/entries", { params: { date } });
      const map = {};
      data.values.forEach((v) => {
        map[`${v.metric_id}|${v.collaborator_id || ""}`] = String(v.value);
      });
      setValues(map);
      setInitial(map);
      setMeta({ saved: data.saved, saved_at: data.saved_at });
    } catch (e) {
      toast.error("Erreur de chargement de la journée");
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const setVal = (key, v) => setValues((prev) => ({ ...prev, [key]: v }));

  const dirtyKeys = Object.keys({ ...values, ...initial }).filter(
    (k) => (values[k] ?? "") !== (initial[k] ?? "")
  );

  const save = async () => {
    if (dirtyKeys.length === 0) {
      toast.info("Aucune modification à enregistrer");
      return;
    }
    const payload = dirtyKeys.map((k) => {
      const [metric_id, collab] = k.split("|");
      const raw = (values[k] ?? "").toString().replace(",", ".").trim();
      return {
        metric_id,
        collaborator_id: collab || null,
        value: raw === "" ? null : Number(raw),
      };
    });
    if (payload.some((p) => p.value !== null && Number.isNaN(p.value))) {
      toast.error("Une valeur saisie n'est pas un nombre valide");
      return;
    }
    setSaving(true);
    try {
      const { data } = await apiClient.put("/performance/entries", { date, values: payload });
      toast.success(`Journée du ${date.split("-").reverse().join("/")} enregistrée — ${data.saved} valeur(s)`);
      await load();
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur à l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const manualFor = (serviceId) =>
    metrics.filter((m) => m.service_id === serviceId && m.source === "MANUAL");
  const autoFor = (serviceId) =>
    metrics.filter((m) => m.service_id === serviceId && m.source !== "MANUAL");

  return (
    <>
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-5">
        <div className="flex items-center gap-2 h-10 px-3 border border-zinc-300 bg-white">
          <CalendarBlank size={15} weight="bold" className="text-zinc-400" />
          <input
            type="date"
            value={date}
            max={todayISO()}
            onChange={(e) => setDate(e.target.value)}
            className="text-sm outline-none bg-transparent font-mono"
            data-testid="saisie-date"
          />
        </div>
        <div className="inline-flex border border-zinc-300 bg-white">
          <button onClick={() => setMode("service")} data-testid="saisie-mode-service" className={`px-4 h-10 text-sm ${mode === "service" ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-50"}`}>Par service</button>
          <button onClick={() => setMode("collab")} data-testid="saisie-mode-collab" className={`px-4 h-10 text-sm ${mode === "collab" ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-50"}`}>Par collaborateur</button>
        </div>
        {meta.saved && (
          <span className="inline-flex items-center gap-1.5 text-xs text-green-700" data-testid="saisie-saved-badge">
            <CheckCircle size={14} weight="fill" /> Journée déjà enregistrée — modifiable
          </span>
        )}
        <div className="flex-1" />
        {dirtyKeys.length > 0 && (
          <span className="text-xs text-amber-700" data-testid="saisie-dirty-count">{dirtyKeys.length} modification(s) non enregistrée(s)</span>
        )}
        <Button variant="outline" onClick={load} disabled={loading} className="rounded-sm h-10 border-zinc-300 gap-2 text-xs" data-testid="saisie-reload">
          <ArrowClockwise size={15} weight="bold" /> Recharger
        </Button>
        <Button onClick={save} disabled={saving} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm h-10 gap-2" data-testid="saisie-save">
          <FloppyDisk size={16} weight="bold" /> {saving ? "Enregistrement…" : "Enregistrer la journée"}
        </Button>
      </div>

      {mode === "collab" ? (
        collaborators.length === 0 ? (
          <Empty text="Aucun collaborateur. Ajoutez-les dans l'onglet « Indicateurs & services »." />
        ) : (
          <div className="space-y-4">
            {collaborators.map((c) => {
              const ms = manualFor(c.service_id).filter((m) => m.per_collaborator);
              return (
                <div key={c.id} className="bg-white border border-zinc-200" data-testid={`saisie-collab-${c.id}`}>
                  <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
                    <div className="font-heading font-semibold text-sm">{c.name}</div>
                    <span className="label-tiny text-zinc-400">{services.find((s) => s.id === c.service_id)?.name}</span>
                  </div>
                  {ms.length === 0 ? (
                    <div className="px-4 py-3 text-xs text-zinc-500">Aucun indicateur suivi par collaborateur pour ce service.</div>
                  ) : (
                    <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {ms.map((m) => (
                        <Field key={m.id} metric={m} k={`${m.id}|${c.id}`} values={values} setVal={setVal} initial={initial} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      ) : services.length === 0 ? (
        <Empty text="Aucun service actif. Créez-les dans l'onglet « Indicateurs & services »." />
      ) : (
        <div className="space-y-4">
          {services.map((s) => {
            const manual = manualFor(s.id);
            const auto = autoFor(s.id);
            if (manual.length === 0 && auto.length === 0) return null;
            return (
              <div key={s.id} className="bg-white border border-zinc-200" data-testid={`saisie-service-${s.id}`}>
                <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
                  <div className="font-heading font-semibold text-sm">{s.name}</div>
                  <span className="label-tiny text-zinc-400">
                    {manual.length} à saisir{auto.length ? ` · ${auto.length} automatique${auto.length > 1 ? "s" : ""}` : ""}
                  </span>
                </div>
                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {manual.map((m) => (
                    <Field key={m.id} metric={m} k={`${m.id}|`} values={values} setVal={setVal} initial={initial} />
                  ))}
                  {auto.map((m) => (
                    <div key={m.id} className="opacity-70">
                      <div className="label-tiny text-zinc-400 mb-1 flex items-center gap-1.5">{m.name} <SourceTag source={m.source} /></div>
                      <div className="h-10 border border-dashed border-zinc-300 bg-zinc-50 flex items-center px-3 text-xs text-zinc-500 truncate">
                        {m.formula ? prettyFormula(m.formula, metrics) : "via connecteur"}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

function Field({ metric, k, values, setVal, initial }) {
  const changed = (values[k] ?? "") !== (initial[k] ?? "");
  return (
    <div>
      <div className="label-tiny text-zinc-400 mb-1 flex items-center gap-1.5 truncate">
        <span className="truncate">{metric.name}</span>
        <span className="text-zinc-300">·</span>
        <span className="normal-case text-zinc-400 shrink-0">{UNIT_LABEL[metric.unit]}</span>
        {metric.snapshot && <span className="normal-case text-zinc-300 shrink-0">état du jour</span>}
      </div>
      <Input
        value={values[k] ?? ""}
        onChange={(e) => setVal(k, e.target.value)}
        placeholder="—"
        inputMode="decimal"
        className={`h-10 rounded-sm tabular-nums ${changed ? "border-amber-400 bg-amber-50" : "border-zinc-300"}`}
        data-testid={`saisie-input-${k}`}
      />
    </div>
  );
}

const Empty = ({ text }) => (
  <div className="bg-white border border-dashed border-zinc-300 px-5 py-10 text-center text-sm text-zinc-500">{text}</div>
);
